import { Badge, HStack, Text, VStack } from "@chakra-ui/layout";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { ErrorBoundaryFallbackProps } from "@suspensive/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  creditFacilityPaydownTrancheBreakdownTableRetrieve,
  CreditFacilityPaydownTrancheBreakdownTableRetrieveMutationBody,
} from "gen/endpoints/credit-facility/credit-facility";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { GenericErrorView } from "shared/components/empty/GenericErrorView";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { DataTable } from "shared/components/table/data/DataTable";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { useBoolean } from "usehooks-ts";

export const breakdownTableQueryKey = (
  fundFamilyId: string,
  creditFacilityId: string,
  fundFamilyEventId: string,
  payloadKey: CreditFacilityPaydownTrancheBreakdownTableRetrieveMutationBody,
) =>
  [
    `/fund-family/${fundFamilyId}/credit-facility/${creditFacilityId}/${fundFamilyEventId}/paydown/tranches`,
    payloadKey,
  ] as const;

interface BreakdownTableProps {
  transactionCodeId: string;
  investmentId: string;
  fundAmounts: Record<string, string>;
  loanId: string;
}
export interface PaydownTrancheBreakdownModalProps extends BreakdownTableProps {
  onCloseComplete?: () => void;
  useName: string;
  investmentType: string;
  loanName: string;
}

export const PaydownTrancheBreakdownModal: React.FC<
  PaydownTrancheBreakdownModalProps
> = ({
  onCloseComplete,
  useName,
  investmentId,
  transactionCodeId,
  loanId,
  loanName,
  investmentType,
  fundAmounts,
}) => {
  const { t } = useTranslation(["creditFacility", "common"]);

  const { value: isOpen, setFalse: close } = useBoolean(true);
  const cancelRef = useRef(null);

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      size="6xl"
      onCloseComplete={onCloseComplete}
      initialFocusRef={cancelRef}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <VStack alignItems="flex-start" gap="8px">
            <HStack>
              <Text>
                {t("creditFacility:paydown.trancheBreakdownTitle", {
                  useName,
                })}
              </Text>
              <Badge>{loanName}</Badge>
              {investmentId && <Badge>{investmentType}</Badge>}
            </HStack>
            <Text fontSize="12px" color="gray.500">
              {t("creditFacility:paydown.trancheBreakdownDescription")}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <QueryBoundary ErrorComponent={ModalError}>
          <ModalBody pb="20px">
            <TrancheBreakdownTable
              transactionCodeId={transactionCodeId}
              investmentId={investmentId}
              loanId={loanId}
              fundAmounts={fundAmounts}
            />
          </ModalBody>
        </QueryBoundary>
      </ModalContent>
    </Modal>
  );
};

const TrancheBreakdownTable: React.FC<BreakdownTableProps> = ({
  transactionCodeId,
  investmentId,
  loanId,
  fundAmounts,
}) => {
  const { fundFamilyId, creditFacilityId, fundFamilyEventId } =
    useRequiredStringParams();
  const { data: trancheBreakdownTable } = useSuspenseQuery({
    queryKey: breakdownTableQueryKey(
      fundFamilyId,
      creditFacilityId,
      fundFamilyEventId,
      {
        investment_id: investmentId,
        transaction_code_id: transactionCodeId,
        loan_id: loanId,
        fund_amounts: fundAmounts,
      },
    ),
    queryFn: () =>
      creditFacilityPaydownTrancheBreakdownTableRetrieve(
        fundFamilyId,
        creditFacilityId,
        fundFamilyEventId,
        {
          investment_id: investmentId,
          transaction_code_id: transactionCodeId,
          loan_id: loanId,
          fund_amounts: fundAmounts,
        },
      ),
  });
  return (
    <TableBox isFullWidth={true}>
      <TableBoxBody>
        <DataTable data={trancheBreakdownTable} />
      </TableBoxBody>
    </TableBox>
  );
};

const ModalError: React.FC<ErrorBoundaryFallbackProps> = ({ error }) => (
  <ModalBody p="24px">
    <GenericErrorView error={error} />
  </ModalBody>
);
